import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isApiHttpError } from '../../api/apiErrors';
import type { ProviderId } from '../../api/apiSchemas';
import { useIdentities, useLinkStart, useUnlinkIdentity } from '../../auth/authQueries';
import { getPresentation, linkableProviders } from '../../auth/providerPresentation';
import { ConnectionCard } from '../../components/account/ConnectionCard';
import { UnlinkDialog } from '../../components/account/UnlinkDialog';
import { Alert, Card, Skeleton, pushToast } from '../../components/ui';
import { browserNavigation } from '../../lib/browserNavigation';
import { env } from '../../lib/env';
import { track } from '../../lib/logger';

function apiOrigin(): string {
  try {
    return new URL(env.apiBaseUrl).origin;
  } catch {
    return window.location.origin;
  }
}

/** Only follow a link-start redirect to the backend over https (spec §10.9). */
function isBackendRedirect(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.origin === apiOrigin();
  } catch {
    return false;
  }
}

export default function ConnectionsPage() {
  const identities = useIdentities(true);
  const linkStart = useLinkStart();
  const unlink = useUnlinkIdentity();
  const [search] = useSearchParams();

  const [dialogProvider, setDialogProvider] = useState<ProviderId | null>(null);
  const [lastAuthBlocked, setLastAuthBlocked] = useState(false);

  const justLinked = search.get('linked');
  const linkingProvider = linkStart.isPending ? linkStart.variables?.provider : undefined;

  const available = useMemo(
    () => (identities.data ? linkableProviders(identities.data.availableToLink) : []),
    [identities.data],
  );

  function handleLink(provider: ProviderId): void {
    track('auth_identity_link_started', { provider });
    linkStart.mutate(
      { provider, body: { returnRoute: '/account/connections' } },
      {
        onSuccess: ({ redirectUrl }) => {
          if (isBackendRedirect(redirectUrl)) browserNavigation.assign(redirectUrl);
          else pushToast('Không thể bắt đầu liên kết. Vui lòng thử lại.', 'error');
        },
        onError: () => pushToast('Không thể bắt đầu liên kết. Vui lòng thử lại.', 'error'),
      },
    );
  }

  function confirmUnlink(): void {
    if (!dialogProvider) return;
    const provider = dialogProvider;
    unlink.mutate(provider, {
      onSuccess: () => {
        track('auth_identity_unlink_succeeded', { provider });
        pushToast('Đã gỡ liên kết.', 'success');
        setDialogProvider(null);
      },
      onError: (error) => {
        const status = isApiHttpError(error) ? error.status : 0;
        const code = isApiHttpError(error) ? error.code : 'UNKNOWN';
        track('auth_identity_unlink_failed', { provider, httpStatus: status, errorCode: code });
        setDialogProvider(null);
        if (status === 409 && code === 'LAST_AUTH_METHOD') {
          setLastAuthBlocked(true);
        } else if (status === 404) {
          pushToast('Kết nối không còn tồn tại.', 'info');
        } else if (status === 403) {
          pushToast('Có lỗi bảo mật. Vui lòng thử lại.', 'error');
        } else if (status === 409) {
          pushToast('Thao tác bị xung đột. Vui lòng thử lại.', 'error');
        } else {
          pushToast('Không thể gỡ liên kết. Vui lòng thử lại.', 'error');
        }
      },
    });
  }

  return (
    <div className="stack">
      <h1>Phương thức đăng nhập</h1>

      {justLinked ? (
        <Alert tone="success" live>
          Đã liên kết{' '}
          {getPresentation(justLinked === 'linkedin' ? 'linkedin' : 'google').displayName}.
        </Alert>
      ) : null}

      {lastAuthBlocked ? (
        <Alert tone="error" live>
          Bạn không thể gỡ liên kết phương thức đăng nhập cuối cùng. Hãy liên kết thêm một phương
          thức khác trước.
        </Alert>
      ) : null}

      {identities.isLoading ? (
        <Card>
          <Skeleton height="80px" />
        </Card>
      ) : null}

      {identities.isError ? (
        <Alert tone="error">Không thể tải danh sách kết nối. Vui lòng thử lại.</Alert>
      ) : null}

      {identities.data ? (
        <div className="stack">
          {identities.data.identities.map((identity) => (
            <ConnectionCard
              key={identity.provider}
              kind="linked"
              identity={identity}
              busy={unlink.isPending && dialogProvider === identity.provider}
              onUnlink={(provider) => {
                setLastAuthBlocked(false);
                setDialogProvider(provider);
              }}
            />
          ))}

          {available.map((provider) => (
            <ConnectionCard
              key={provider.id}
              kind="available"
              provider={provider}
              busy={linkingProvider === provider.id}
              onLink={handleLink}
            />
          ))}

          {identities.data.identities.length === 0 && available.length === 0 ? (
            <Alert tone="info">Chưa có phương thức đăng nhập nào để hiển thị.</Alert>
          ) : null}
        </div>
      ) : null}

      <UnlinkDialog
        open={dialogProvider !== null}
        providerName={dialogProvider ? getPresentation(dialogProvider).displayName : ''}
        busy={unlink.isPending}
        onConfirm={confirmUnlink}
        onCancel={() => setDialogProvider(null)}
      />
    </div>
  );
}
